import { Users, X } from 'lucide-react';
import { useState } from 'react';
import { EndUserDetailModal } from '../components/EndUserDetailModal';
import { EndUsersTable } from '../components/EndUsersTable';
import { useEndUsers } from '../hooks/useEndUsers';
import type { EndUserAuthProvider, EndUserStatus } from '../types/end-users.types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ahkinia/Select/select';
import { SectionHeader } from '@/shared/components/ui/header';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

const STATUS_OPTIONS: { value: EndUserStatus; label: string }[] = [
  { value: 'active', label: 'Activo' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'blocked', label: 'Bloqueado' },
  { value: 'inactive', label: 'Inactivo' },
];

const AUTH_PROVIDER_OPTIONS: { value: EndUserAuthProvider; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'google', label: 'Google' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'microsoft', label: 'Microsoft' },
];

export default function EndUsersPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState<EndUserStatus | ''>('');
  const [authProvider, setAuthProvider] = useState<EndUserAuthProvider | ''>('');

  const { endUsers, pagination, isLoading } = useEndUsers({
    page,
    pageSize,
    searchTerm: searchTerm || undefined,
    status: status || undefined,
    authProvider: authProvider || undefined,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatus('');
    setAuthProvider('');
    setPage(1);
  };

  const hasFilters = searchTerm || status || authProvider;
  const hasData = endUsers.length > 0;

  return (
    <div data-testid="end-users-page" className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<Users />}
        title="Clientes"
        subtitle="Listado de usuarios registrados en la plataforma"
      />

      <div className="flex items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary">Buscar</label>
          <Input
            data-testid="search-end-users-input"
            placeholder="Nombre o correo..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="w-60"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary">Estado</label>
          <Select
            value={status}
            onValueChange={(val) => {
              setStatus(val as EndUserStatus | '');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-37.5" data-testid="filter-status-select">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-primary">Proveedor</label>
          <Select
            value={authProvider}
            onValueChange={(val) => {
              setAuthProvider(val as EndUserAuthProvider | '');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-37.5" data-testid="filter-provider-select">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              {AUTH_PROVIDER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasFilters && (
          <UnifiedButton
            data-testid="clear-filters-btn"
            variant="outline"
            onClick={handleClearFilters}
          >
            <X size={16} />
            Limpiar filtros
          </UnifiedButton>
        )}
      </div>

      {isLoading && (
        <div data-testid="end-users-loading" className="flex justify-center py-12">
          <span className="text-primary text-sm">Cargando usuarios...</span>
        </div>
      )}

      {!isLoading && hasData && (
        <EndUsersTable
          data={endUsers}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onViewDetail={setSelectedUserId}
        />
      )}

      {!isLoading && !hasData && (
        <div
          data-testid="end-users-empty-state"
          className="flex flex-col items-center justify-center py-12 rounded-xl bg-background border-outline"
        >
          <Users size={48} className="text-primary mb-4 opacity-50" />
          <p className="text-primary">No se encontraron usuarios con los filtros aplicados.</p>
        </div>
      )}

      <EndUserDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
    </div>
  );
}
